import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ViewAsProvider } from './context/ViewAsContext';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './context/ToastContext';
import { BadgeSettingsProvider } from './context/BadgeSettingsContext';
import { EventProvider } from './context/EventContext';
import './styles/print.css'; // Import print-specific styles
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Events from './pages/Events';
import Attendees from './pages/Attendees';
import CustomFields from './pages/CustomFields';
import CheckIn from './pages/CheckIn';
import PrintStation from './pages/PrintStation';
import BadgeTemplates from './pages/BadgeTemplates';
import StickerTemplates from './pages/StickerTemplates';
import EventSettings from './pages/EventSettings';
import Campaigns from './pages/Campaigns';
import Analytics from './pages/Analytics';
import UserManagement from './pages/UserManagement';
import Organizations from './pages/Organizations';
import Administrators from './pages/Administrators';
import SystemSettings from './pages/SystemSettings';
import OrganizationSettings from './pages/OrganizationSettings';
// Tickets page is now in EventSettings
import SelfRegistration from './pages/SelfRegistration';
import UsherRegistration from './pages/UsherRegistration';
import AttendeeDetail from './pages/AttendeeDetail';
import Loading from './components/common/Loading';
import RegistrationTest from './components/debug/RegistrationTest';
import QRCodeTest from './components/debug/QRCodeTest';
import AttendeeDetailTest from './components/debug/AttendeeDetailTest';
import ShortUrlRedirect from './components/common/ShortUrlRedirect';
import { HelpGuide } from './pages/HelpGuide';
// import ApiDebugConsole from './components/debug/ApiDebugConsole';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
    },
  },
});

const PrivateRoute = ({ children, requireAdmin = false, requireSuperAdmin = false }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <Loading fullScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Redirect attendee role to self-registration page
  if (user?.role === 'attendee') {
    return <Navigate to="/self-register" />;
  }

  // Check super admin requirement (most restrictive)
  if (requireSuperAdmin && user?.role !== 'super_admin') {
    return <Navigate to="/dashboard" />;
  }

  // Check admin requirement
  if (requireAdmin && !['super_admin', 'event_admin'].includes(user?.role)) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

// Smart router: renders UsherRegistration when ?usher is present in URL
const RegistrationRouter: React.FC = () => {
  const [searchParams] = useSearchParams();
  const isUsher = searchParams.has('usher');
  return isUsher ? <UsherRegistration /> : <SelfRegistration />;
};

const AppRoutes = () => {

  return (
    <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        
        {/* Public Self-Registration Route (for kiosks). Append ?usher for usher fast-entry mode */}
        <Route path="/self-register" element={<RegistrationRouter />} />
        
        {/* Token-based Registration Route (secure, resettable links) */}
        <Route path="/register" element={<RegistrationRouter />} />
        
        {/* Public Attendee Detail Route (for QR code scanning) */}
        <Route path="/attendee/:attendeeId" element={<AttendeeDetail />} />
        
        {/* Short URL Redirect Route */}
        <Route path="/s/:shortCode" element={<ShortUrlRedirect />} />
        
        {/* Debug/Test Routes */}
        <Route path="/test-registration" element={<RegistrationTest />} />
        <Route path="/test-qr" element={<QRCodeTest />} />
        <Route path="/test-attendee-detail" element={<AttendeeDetailTest />} />
        
        {/* Protected Routes */}
        <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
        <Route path="/users" element={<PrivateRoute><UserManagement /></PrivateRoute>} />
        <Route path="/organizations" element={<PrivateRoute requireSuperAdmin={true}><Organizations /></PrivateRoute>} />
        <Route path="/administrators" element={<PrivateRoute requireSuperAdmin={true}><Administrators /></PrivateRoute>} />
        <Route path="/attendees" element={<PrivateRoute><Attendees /></PrivateRoute>} />
        <Route
          path="/events"
          element={
            <PrivateRoute requireAdmin>
              <Events />
            </PrivateRoute>
          }
        />

        <Route path="/event-settings" element={<PrivateRoute requireAdmin={true}><EventSettings /></PrivateRoute>} />
        <Route path="/tickets" element={<Navigate to="/event-settings#ticketing" replace />} />
        <Route path="/custom-fields" element={<Navigate to="/event-settings#registration" replace />} />
        <Route path="/fields" element={<Navigate to="/event-settings#registration" replace />} />
        <Route path="/check-in" element={<PrivateRoute><CheckIn /></PrivateRoute>} />
        <Route path="/print-station" element={<PrivateRoute><PrintStation /></PrivateRoute>} />
        <Route path="/badges" element={<Navigate to="/event-settings#badges" replace />} />
        <Route path="/stickers" element={<Navigate to="/event-settings#stickers" replace />} />
        <Route path="/campaigns" element={<PrivateRoute requireAdmin={true}><Campaigns /></PrivateRoute>} />
        <Route path="/email-campaigns" element={<Navigate to="/campaigns#email" replace />} />
        <Route path="/sms-campaigns" element={<Navigate to="/campaigns#sms" replace />} />
        <Route path="/analytics" element={<PrivateRoute><Analytics /></PrivateRoute>} />
        <Route path="/system-settings" element={<PrivateRoute requireSuperAdmin={true}><SystemSettings /></PrivateRoute>} />
        <Route path="/organization-settings" element={<PrivateRoute requireAdmin={true}><OrganizationSettings /></PrivateRoute>} />
        <Route path="/help-guide" element={<PrivateRoute><HelpGuide /></PrivateRoute>} />
        
        {/* Redirect old install route to login */}
        <Route path="/install" element={<Navigate to="/login" replace />} />
        
        <Route path="/" element={<Navigate to="/dashboard" />} />
        <Route path="*" element={<Navigate to="/dashboard" />} />
      </Routes>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider>
          <ToastProvider>
            <BadgeSettingsProvider>
              <AuthProvider>
                <EventProvider>
                  <ViewAsProvider>
                    <AppRoutes />
                    {/* Debug Console - Only show in development */}
                    {/* {import.meta.env.VITE_DEBUG === 'true' && <ApiDebugConsole />} */}
                  </ViewAsProvider>
                </EventProvider>
              </AuthProvider>
            </BadgeSettingsProvider>
          </ToastProvider>
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
